import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { toast } from "sonner";
import { ArrowLeft, LayoutGrid, Sparkles, Receipt, Shield } from "lucide-react";
import { LogoMark } from "@/components/app/Brand";

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";

function loadGoogleScript() {
  return new Promise((resolve) => {
    if (window.google?.accounts) { resolve(); return; }
    const s = document.createElement("script");
    s.src = "https://accounts.google.com/gsi/client";
    s.async = true;
    s.defer = true;
    s.onload = resolve;
    document.head.appendChild(s);
  });
}

const FEATURES = [
  { icon: LayoutGrid, label: "Projects & tasks" },
  { icon: Sparkles,   label: "AI assistant" },
  { icon: Receipt,    label: "Invoicing & finance" },
  { icon: Shield,     label: "Compliance vault" },
];

const AuthPage = () => {
  const [params] = useSearchParams();
  const nextPath = params.get("next");
  const prefillEmail = params.get("prefill") || "";
  const [mode, setMode] = useState(params.get("mode") === "signup" ? "signup" : "login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState(prefillEmail);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [ssoOption, setSsoOption] = useState(null);
  const [mfaChallengeId, setMfaChallengeId] = useState(null);
  const [mfaCode, setMfaCode] = useState("");
  const { login, verifyMfaLogin, signup, googleLogin: authGoogleLogin } = useAuth();
  const navigate = useNavigate();

  const checkSSO = async () => {
    if (mode !== "login" || !email.includes("@")) { setSsoOption(null); return; }
    try {
      const { data } = await api.get(`/sso/resolve?email=${encodeURIComponent(email)}`);
      setSsoOption(data.sso_available ? data : null);
    } catch {
      setSsoOption(null);
    }
  };

  const safeNext = (() => {
    if (nextPath && nextPath.startsWith("/") && !nextPath.startsWith("//")) return nextPath;
    return "/dashboard";
  })();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "signup") {
        await signup(name, email, password);
        toast.success("Account created");
      } else {
        const result = await login(email, password);
        if (result?.mfa_required) {
          setMfaChallengeId(result.challenge_id);
          setBusy(false);
          return;
        }
        toast.success("Welcome back");
      }
      navigate(safeNext);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Authentication failed");
    } finally {
      setBusy(false);
    }
  };

  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await verifyMfaLogin(mfaChallengeId, mfaCode.trim());
      toast.success("Welcome back");
      navigate(safeNext);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid code");
    } finally {
      setBusy(false);
    }
  };

  const handleGoogleCredential = useCallback(async (credentialResponse) => {
    setBusy(true);
    try {
      const result = await authGoogleLogin(credentialResponse.credential);
      if (result?.mfa_required) {
        setMfaChallengeId(result.challenge_id);
        setBusy(false);
        return;
      }
      navigate(safeNext);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Google sign-in failed");
    } finally {
      setBusy(false);
    }
  }, [authGoogleLogin, navigate, safeNext]);

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;
    loadGoogleScript().then(() => {
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleGoogleCredential,
        auto_select: false,
      });
    });
  }, [handleGoogleCredential]);

  const googleLogin = async () => {
    if (!GOOGLE_CLIENT_ID) {
      toast.error("Google sign-in is not configured. Please contact support.");
      return;
    }
    await loadGoogleScript();
    window.google.accounts.id.prompt((notification) => {
      if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
        window.google.accounts.oauth2.initTokenClient({
          client_id: GOOGLE_CLIENT_ID,
          scope: "email profile openid",
          callback: () => {},
        });
        window.google.accounts.id.renderButton(
          document.getElementById("g-signin-btn"),
          { theme: "filled_black", size: "large", width: 400 }
        );
        document.getElementById("g-signin-btn")?.querySelector("div[role=button]")?.click();
      }
    });
  };

  const inputCls = "rounded-xl mt-1.5 h-11 bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400 focus:ring-violet-300 focus:border-violet-400 transition-colors";

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">

      {/* ── Left panel ── */}
      <div className="hidden lg:flex flex-col justify-between p-12 bg-gradient-to-br from-violet-700 via-violet-600 to-indigo-700 relative overflow-hidden">
        {/* Decorative circles */}
        <div className="absolute top-0 right-0 w-80 h-80 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/3 pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/3 pointer-events-none" />
        <div className="absolute top-1/2 left-1/2 w-48 h-48 bg-white/5 rounded-full -translate-x-1/2 -translate-y-1/2 pointer-events-none" />

        {/* Logo */}
        <Link to="/" className="flex items-center gap-2.5 w-fit" data-testid="auth-brand-link">
          <div className="w-8 h-8 rounded-xl bg-white/20 grid place-items-center">
            <LogoMark size={18} color="#fff" />
          </div>
          <span className="font-bold text-lg text-white tracking-tight">
            Combined<span className="text-violet-200">Work</span>
          </span>
        </Link>

        {/* Hero text */}
        <div className="relative z-10">
          <h1 className="font-display font-bold text-5xl tracking-tight leading-[1.1] text-white max-w-sm">
            The operating system for project delivery.
          </h1>
          <p className="mt-4 text-violet-200 text-base max-w-xs leading-relaxed">
            AI-powered workspace for Agile, Waterfall and Hybrid teams.
          </p>

          {/* Feature pills */}
          <div className="mt-10 flex flex-wrap gap-2">
            {FEATURES.map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-3 py-1.5">
                <Icon className="w-3.5 h-3.5 text-violet-200" strokeWidth={1.75} />
                <span className="text-xs font-medium text-white">{label}</span>
              </div>
            ))}
          </div>

          {/* Stats */}
          <div className="mt-10 grid grid-cols-3 gap-6 max-w-xs">
            {[["10×", "Faster docs"], ["1", "Source of truth"], ["24/7", "AI assistant"]].map(([n, l]) => (
              <div key={l} className="border-t border-white/20 pt-3">
                <div className="font-bold text-3xl text-white">{n}</div>
                <div className="text-[10px] uppercase tracking-widest text-violet-300 mt-1">{l}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="text-xs uppercase tracking-widest text-violet-400/60 relative z-10">© 2026 CombinedWork</div>
      </div>

      {/* ── Right panel ── */}
      <div className="flex flex-col p-6 sm:p-12 bg-white">
        <Link
          to="/"
          className="text-xs font-medium text-gray-400 flex items-center gap-1.5 hover:text-violet-600 transition-colors w-fit"
          data-testid="back-home-link"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back home
        </Link>

        <div className="flex-1 flex items-center">
          <div className="w-full max-w-sm mx-auto">

            {mfaChallengeId ? (
              <>
                {/* Heading */}
                <div className="mb-8">
                  <p className="text-xs font-semibold uppercase tracking-widest text-violet-500 mb-2">Two-factor authentication</p>
                  <h2 className="font-display font-bold text-4xl text-gray-900 tracking-tight">Enter your code.</h2>
                  <p className="mt-2 text-sm text-gray-400">Enter the 6-digit code from your authenticator app, or one of your backup codes.</p>
                </div>

                <form onSubmit={handleMfaSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="mfa-code" className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Code</Label>
                    <Input
                      id="mfa-code"
                      data-testid="mfa-code-input"
                      value={mfaCode}
                      onChange={(e) => setMfaCode(e.target.value)}
                      required
                      autoFocus
                      placeholder="123456"
                      className={inputCls}
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={busy}
                    className="w-full h-12 bg-violet-600 hover:bg-violet-700 text-white font-bold text-sm rounded-full transition-all hover:scale-[1.01] disabled:opacity-50 shadow-md shadow-violet-200 mt-2"
                    data-testid="mfa-submit-btn"
                  >
                    {busy ? "Verifying…" : "Verify"}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setMfaChallengeId(null); setMfaCode(""); }}
                    className="w-full text-center text-xs text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    Back to sign in
                  </button>
                </form>
              </>
            ) : (
              <>
            {/* Heading */}
            <div className="mb-8">
              <p className="text-xs font-semibold uppercase tracking-widest text-violet-500 mb-2">
                {mode === "signup" ? "Create account" : "Welcome back"}
              </p>
              <h2 className="font-display font-bold text-4xl text-gray-900 tracking-tight">
                {mode === "signup" ? "Start delivering." : "Sign in."}
              </h2>
            </div>

            {/* Hidden GIS button */}
            <div id="g-signin-btn" className="hidden" />

            {/* Google button */}
            <button
              type="button"
              onClick={googleLogin}
              disabled={busy}
              className="w-full h-12 flex items-center justify-center gap-3 border-2 border-gray-200 rounded-xl bg-white text-gray-700 text-sm font-semibold hover:border-gray-300 hover:bg-gray-50 transition-colors disabled:opacity-50"
              data-testid="google-auth-btn"
            >
              <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.25 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </button>

            {/* Divider */}
            <div className="flex items-center gap-3 my-6">
              <div className="flex-1 h-px bg-gray-100" />
              <span className="text-[11px] font-medium uppercase tracking-widest text-gray-400">or with email</span>
              <div className="flex-1 h-px bg-gray-100" />
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {mode === "signup" && (
                <div>
                  <Label htmlFor="name" className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Name</Label>
                  <Input
                    id="name"
                    data-testid="signup-name-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    placeholder="Your full name"
                    className={inputCls}
                  />
                </div>
              )}

              <div>
                <Label htmlFor="email" className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Email</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="auth-email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onBlur={checkSSO}
                  required
                  placeholder="you@company.com"
                  className={inputCls}
                />
              </div>

              {ssoOption && (
                <button
                  type="button"
                  onClick={() => { window.location.href = ssoOption.login_url; }}
                  className="w-full h-11 flex items-center justify-center gap-2 border-2 border-violet-200 rounded-xl bg-violet-50 text-violet-700 text-sm font-semibold hover:bg-violet-100 transition-colors"
                  data-testid="sso-continue-btn"
                >
                  <Shield className="w-4 h-4" /> Continue with {ssoOption.display_name} SSO
                </button>
              )}

              <div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Password</Label>
                  {mode === "login" && (
                    <Link to="/forgot-password" className="text-xs font-semibold text-violet-500 hover:text-violet-700 transition-colors" data-testid="forgot-password-link">
                      Forgot?
                    </Link>
                  )}
                </div>
                <Input
                  id="password"
                  type="password"
                  data-testid="auth-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  placeholder="••••••••"
                  className={inputCls}
                />
              </div>

              <button
                type="submit"
                disabled={busy}
                className="w-full h-12 bg-violet-600 hover:bg-violet-700 text-white font-bold text-sm rounded-full transition-all hover:scale-[1.01] disabled:opacity-50 shadow-md shadow-violet-200 mt-2"
                data-testid="auth-submit-btn"
              >
                {busy ? "Please wait…" : mode === "signup" ? "Create account" : "Sign in"}
              </button>
            </form>

            {/* Switch mode */}
            <p className="mt-6 text-sm text-center text-gray-400">
              {mode === "signup" ? (
                <>Already have an account?{" "}
                  <button onClick={() => setMode("login")} className="text-violet-600 font-semibold hover:text-violet-800 transition-colors" data-testid="switch-to-login">Sign in</button>
                </>
              ) : (
                <>New to CombinedWork?{" "}
                  <button onClick={() => setMode("signup")} className="text-violet-600 font-semibold hover:text-violet-800 transition-colors" data-testid="switch-to-signup">Create account</button>
                </>
              )}
            </p>

            {mode === "signup" && (
              <p className="mt-4 text-center text-[11px] text-gray-400">
                By creating an account you agree to our{" "}
                <Link to="/terms" className="text-violet-500 hover:underline">Terms</Link> and{" "}
                <Link to="/privacy" className="text-violet-500 hover:underline">Privacy Policy</Link>.
              </p>
            )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
